import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Lenis from 'lenis';

const EASE = [0.22, 1, 0.36, 1];

// Global momentum smooth-scrolling. Mounted once from Layout.
export function useSmoothScroll() {
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.4,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

// Scroll-triggered reveal. Fades + slides content into view once.
export function Reveal({ children, className = '', delay = 0, y = 26, as = 'div', once = true, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: '-70px' }}
      transition={{ duration: 0.75, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Container that staggers its direct <Reveal>/motion children on view.
export function RevealStagger({ children, className = '', stagger = 0.08, once = true, ...rest }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, margin: '-60px' }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({ children, className = '', y = 30, ...rest }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// Signature on-load moment: line-by-line masked reveal for the hero headline.
// `lines` is an array of ReactNode. Each line rises from behind a clip mask.
export function MaskedHeading({ lines, className = '', lineClassName = '', delay = 0.15, testId }) {
  return (
    <h1 className={className} data-testid={testId}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: '115%' }}
            animate={{ y: '0%' }}
            transition={{ duration: 0.95, delay: delay + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

// Slow editorial marquee. Duplicates items so the loop is seamless.
export function Marquee({ items, className = '', speed = 34 }) {
  const doubled = [...items, ...items];
  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        className="flex w-max items-center"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: speed, ease: 'linear', repeat: Infinity }}
      >
        {doubled.map((item, i) => (
          <span key={i} className="flex items-center">
            <span className="font-serif text-2xl md:text-4xl lg:text-5xl text-brand-bone/90 px-6 md:px-9 tracking-tight whitespace-nowrap">
              {item}
            </span>
            <span className="text-brand-maroon text-2xl md:text-4xl select-none">◆</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}
